import {
  APIProvider,
  AdvancedMarker,
  InfoWindow,
  Map,
} from "@vis.gl/react-google-maps";
import { memo, useEffect, useMemo, useState } from "react";

import { useRouteDriverRoute } from "@web-app/api";
import { DateTimeTz, Marker, Skeleton } from "@web-app/components";
import config from "@web-app/config";
import useOrderSidebar from "@web-app/pages/common/OrdersSidebar/useOrderSidebar";
import { Order, OrderDeliveryType, OrdersOrder, Route } from "@web-app/types";
import { getMetersToMiles } from "@web-app/utils/helpers";

import {
  getOrderIcon,
  getRoutePolylineOptions,
} from "../../common/DispatchMap/utils";
import {
  EVENT_COLORS,
  EVENT_LEGEND,
  EventBase,
  EventMarker,
  MapPolyline,
  MapSetup,
  findClosestLocation,
  getRoutePositionColor,
} from "../../common/OrdersSidebar/OrderDetailsPanel/driverRouteUtils";
import { useDriverRouteMapState } from "../../common/OrdersSidebar/OrderDetailsPanel/useDriverRouteMapState";

/**
 * Creates a map from order ID to its 1-based position in route.ordersOrder
 */
export const createOrderIndexMap = (
  ordersOrder: OrdersOrder[] | undefined,
): Record<string, number> => {
  const map: Record<string, number> = {};
  if (ordersOrder) {
    ordersOrder.forEach((item) => {
      map[item.id] = item.index + 1;
    });
  }
  return map;
};

export const generateEvents = (orders: Order[]) => {
  const events: EventBase[] = [];

  orders.forEach((order) => {
    if (order.type === OrderDeliveryType.ROUTE_OPT_PICKUP) {
      const orderName = order.pickup?.name || order.displayId;
      events.push({
        ...EVENT_COLORS.IN_TRANSIT,
        name: "In Transit",
        orderName,
        timestamp: order.actualStartDateTime,
      });
      events.push({
        ...EVENT_COLORS.PICKUP_ARRIVAL,
        name: "Pickup Arrival Time",
        orderName,
        timestamp: order.geoPickupDateTime,
      });
      events.push({
        ...EVENT_COLORS.ACTUAL_PICKUP,
        name: "Actual Pickup Time",
        orderName,
        timestamp: order.actualPickupDateTime,
      });
    } else if (order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF) {
      const orderName = order.dropoff?.name || order.displayId;
      events.push({
        ...EVENT_COLORS.IN_TRANSIT,
        name: "In Transit",
        orderName,
        timestamp: order.actualStartDateTime,
      });
      events.push({
        ...EVENT_COLORS.DROPOFF_ARRIVAL,
        name: "Drop-off Arrival Time",
        orderName,
        timestamp: order.geoDropoffDateTime,
      });
      events.push({
        ...EVENT_COLORS.ACTUAL_DROPOFF,
        name: "Actual Drop-off Time",
        orderName,
        timestamp: order.actualEndDateTime,
      });
    }
  });

  return events;
};

const RouteDriverRoute = memo(function RouteDriverRoute({
  route,
  orders,
}: {
  orders: Order[];
  route: Route;
}) {
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const { assigneesMap } = useOrderSidebar();
  const [expandedMapInstance, setExpandedMapInstance] =
    useState<google.maps.Map | null>(null);
  const routeId = route.id;
  const routeAssigneeId = route.assigneeId;
  const routeStatus = route.status;
  const assigneeName = assigneesMap.get(routeAssigneeId)?.name || "Unknown";

  const {
    data: driverRoute,
    isSuccess,
    isPending,
  } = useRouteDriverRoute(routeId);
  const effectiveOrders = orders;

  const {
    actualDistance,
    hoveredEventMarker,
    hoveredPoint,
    isExpanded,
    routeCoordinates,
    routePoints,
    setHoveredEventMarker,
    setHoveredPoint,
    setIsExpanded,
  } = useDriverRouteMapState(driverRoute);

  const orderIndexMap = useMemo(
    () => createOrderIndexMap(route.ordersOrder),
    [route.ordersOrder],
  );

  const mapConfig = useMemo(() => {
    const allPickupLocations = effectiveOrders
      .filter((o) => o.type === OrderDeliveryType.ROUTE_OPT_PICKUP)
      .map((o) => o.pickupLocation)
      .filter((loc) => loc && loc.coordinates?.length >= 2);
    const allDropoffLocations = effectiveOrders
      .filter((o) => o.type === OrderDeliveryType.ROUTE_OPT_DROPOFF)
      .map((o) => o.dropoffLocation)
      .filter((loc) => loc && loc.coordinates?.length >= 2);
    const allLocations = [...allPickupLocations, ...allDropoffLocations];

    if (allLocations.length > 1) {
      const lngs = allLocations.map((loc) => loc.coordinates[0]);
      const lats = allLocations.map((loc) => loc.coordinates[1]);
      const bounds = {
        east: Math.max(...lngs),
        north: Math.max(...lats),
        south: Math.min(...lats),
        west: Math.min(...lngs),
      };
      const padding = 0.001;
      return {
        defaultBounds: {
          east: bounds.east + padding,
          north: bounds.north + padding,
          south: bounds.south - padding,
          west: bounds.west - padding,
        },
      };
    } else if (allLocations.length === 1) {
      const location = allLocations[0];
      if (location.coordinates && location.coordinates.length >= 2) {
        return {
          defaultCenter: {
            lat: location.coordinates[1],
            lng: location.coordinates[0],
          },
          defaultZoom: 15,
        };
      }
    }

    return {
      defaultCenter: { lat: 40.7128, lng: -74.006 },
      defaultZoom: 10,
    };
  }, [effectiveOrders]);

  const eventMarkers = useMemo(() => {
    if (!driverRoute || !routePoints.length)
      return { missing: [], placed: [], skipped: [] };

    const events = generateEvents(effectiveOrders);

    const placedEvents: EventMarker[] = [];
    const skippedEvents: EventBase[] = [];
    const missingEvents: EventBase[] = [];

    events.forEach((event) => {
      if (event.timestamp === null) {
        missingEvents.push(event);
      } else {
        const { point: location, timeDiff } = findClosestLocation(
          event.timestamp,
          routePoints,
        );

        if (timeDiff <= 300000) {
          placedEvents.push({
            ...event,
            lat: location.lat,
            lng: location.lng,
            timestamp: event.timestamp,
          });
        } else {
          skippedEvents.push({
            ...event,
            timestamp: event.timestamp,
          });
        }
      }
    });

    return {
      missing: missingEvents,
      placed: placedEvents,
      skipped: skippedEvents,
    };
  }, [driverRoute, routePoints, effectiveOrders]);

  const filteredRoutePoints = useMemo(() => {
    if (!eventMarkers?.placed?.length) return routePoints;

    return routePoints.filter((point) => {
      return !eventMarkers.placed.some(
        (event) =>
          Math.abs(event.lat - point.lat) < 0.0001 &&
          Math.abs(event.lng - point.lng) < 0.0001,
      );
    });
  }, [routePoints, eventMarkers]);

  useEffect(() => {
    if (!mapInstance || !isSuccess || !driverRoute?.locationEntries?.length)
      return;

    const bounds = new google.maps.LatLngBounds();
    const firstEntry = driverRoute.locationEntries[0];
    const lastEntry =
      driverRoute.locationEntries[driverRoute.locationEntries.length - 1];

    bounds.extend({
      lat: firstEntry.coordinates.latitude,
      lng: firstEntry.coordinates.longitude,
    });
    bounds.extend({
      lat: lastEntry.coordinates.latitude,
      lng: lastEntry.coordinates.longitude,
    });

    mapInstance.fitBounds(bounds, 10);
  }, [mapInstance, isSuccess, driverRoute]);

  useEffect(() => {
    if (
      !expandedMapInstance ||
      !isSuccess ||
      !driverRoute?.locationEntries?.length
    )
      return;

    const bounds = new google.maps.LatLngBounds();

    driverRoute.locationEntries.forEach((entry) => {
      bounds.extend({
        lat: entry.coordinates.latitude,
        lng: entry.coordinates.longitude,
      });
    });

    effectiveOrders.forEach((order) => {
      if (
        order.pickupLocation &&
        order.pickupLocation.coordinates.length >= 2
      ) {
        bounds.extend({
          lat: order.pickupLocation.coordinates[1],
          lng: order.pickupLocation.coordinates[0],
        });
      }
      if (
        order.dropoffLocation &&
        order.dropoffLocation.coordinates.length >= 2
      ) {
        bounds.extend({
          lat: order.dropoffLocation.coordinates[1],
          lng: order.dropoffLocation.coordinates[0],
        });
      }
    });

    expandedMapInstance.fitBounds(bounds, 20);
  }, [expandedMapInstance, isSuccess, driverRoute, effectiveOrders]);

  const firstOrder = effectiveOrders[0];
  const timezone = firstOrder?.timezone || "America/New_York";

  if (isPending) {
    return (
      <div className="px-6">
        <Skeleton
          height="h-[250px] rounded-lg"
          text="Fetching route data..."
          width="col-span-12"
        />
        <div className="grid grid-cols-12 gap-4 lg:gap-6 mt-5">
          <div className="col-span-6">
            <Skeleton height="h-[14px]" width="w-[80%]" />
            <Skeleton height="h-[14px] mt-2" width="w-[55%]" />
          </div>
          <div className="col-span-6">
            <Skeleton height="h-[14px]" width="w-[80%]" />
            <Skeleton height="h-[14px] mt-2" width="w-[55%]" />
          </div>
        </div>
      </div>
    );
  }

  if (!isSuccess || !driverRoute) {
    return (
      <div className="flex items-center justify-center h-[250px] text-sm text-gray-400 px-6">
        No driver route data available for this route.
      </div>
    );
  }

  const routeStatusForPolyline = routeStatus as
    "COMPLETE" | "INCOMPLETE" | "INPROGRESS";
  const isAssigned = !!routeAssigneeId;

  return (
    <>
      {!isExpanded && (
        <div className="col-span-12 grid grid-cols-12 gap-4 lg:gap-6 px-4 md:px-6">
          <div className="w-full h-auto md:h-[450px] col-span-12 relative">
            <APIProvider apiKey={config.GOOGLE_MAPS_API_KEY}>
              <div style={{ height: "100%", width: "100%" }}>
                <Map
                  style={{ height: "400px", width: "100%" }}
                  defaultCenter={{ lat: 40.7128, lng: -74.006 }}
                  defaultZoom={13}
                  disableDefaultUI={true}
                  mapId={config.GOOGLE_MAPS_ID}
                >
                  <MapSetup onMapReady={setMapInstance} />
                  {effectiveOrders
                    .filter(
                      (o) =>
                        o.type.includes("PICKUP") &&
                        o.pickupLocation &&
                        o.pickupLocation.coordinates.length >= 2,
                    )
                    .map((order) => (
                      <Marker
                        key={`pickup-${order.id}`}
                        icon={getOrderIcon(order, "PICKUP")}
                        position={{
                          lat: order.pickupLocation.coordinates[1],
                          lng: order.pickupLocation.coordinates[0],
                        }}
                        routePosition={orderIndexMap[order.id]}
                        routePositionColor={getRoutePositionColor(
                          order.statusText,
                        )}
                        scale={1}
                        type="pickup"
                      />
                    ))}
                  {effectiveOrders
                    .filter(
                      (o) =>
                        o.type.includes("DROPOFF") &&
                        o.dropoffLocation &&
                        o.dropoffLocation.coordinates.length >= 2,
                    )
                    .map((order) => (
                      <Marker
                        key={`dropoff-${order.id}`}
                        icon={getOrderIcon(order, "DROPOFF")}
                        position={{
                          lat: order.dropoffLocation.coordinates[1],
                          lng: order.dropoffLocation.coordinates[0],
                        }}
                        routePosition={orderIndexMap[order.id]}
                        routePositionColor={getRoutePositionColor(
                          order.statusText,
                        )}
                        scale={1}
                        type="dropoff"
                      />
                    ))}
                  <MapPolyline
                    polylineOptions={getRoutePolylineOptions(
                      routeStatusForPolyline,
                      1,
                      isAssigned,
                    )}
                    routeCoordinates={routeCoordinates}
                  />
                </Map>
              </div>
            </APIProvider>
            <button
              onClick={() => setIsExpanded(true)}
              className="absolute top-4 right-4 bg-white hover:bg-gray-50 border border-gray-200 rounded-md p-2 shadow-sm transition-colors duration-200"
              title="Expand map"
            >
              <svg
                className="w-4 h-4 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                />
              </svg>
            </button>
          </div>

          <div className="col-span-6 flex items-start flex-col gap-2">
            <div className="text-xs md:text-sm font-medium text-gray-700">
              Estimated Distance
            </div>
            <div className="text-xs md:text-sm text-gray-500">
              {route.estimatedDistance
                ? `${getMetersToMiles(route.estimatedDistance)} miles`
                : "-"}
            </div>
          </div>
          <div className="col-span-6 flex items-start flex-col gap-2">
            <div className="text-xs md:text-sm font-medium text-gray-700">
              Actual Distance
            </div>
            <div className="text-xs md:text-sm text-gray-500">
              {actualDistance}
            </div>
          </div>
        </div>
      )}

      {isExpanded && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full h-full max-w-7xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                Driver Timeline - {assigneeName || "Unassigned"}
              </h2>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                title="Close expanded view"
              >
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4 relative">
              <div className="absolute top-9 right-9 z-10 bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[200px]">
                <div className="text-sm font-semibold text-gray-900 mb-2">
                  Events
                </div>
                {EVENT_LEGEND.map((event, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-xs mb-1"
                  >
                    <div
                      className="w-3 h-3 rounded-full border border-white"
                      style={{ backgroundColor: event.color }}
                    ></div>
                    <span className="text-gray-700">{event.name}</span>
                  </div>
                ))}
              </div>
              <div className="w-full h-full">
                <APIProvider apiKey={config.GOOGLE_MAPS_API_KEY}>
                  <Map
                    style={{ height: "100%", width: "100%" }}
                    className="rounded-lg w-full h-full"
                    defaultCenter={
                      "defaultCenter" in mapConfig
                        ? mapConfig.defaultCenter
                        : undefined
                    }
                    defaultZoom={
                      "defaultZoom" in mapConfig
                        ? mapConfig.defaultZoom
                        : undefined
                    }
                    defaultBounds={
                      "defaultBounds" in mapConfig
                        ? mapConfig.defaultBounds
                        : undefined
                    }
                    disableDefaultUI={true}
                    mapId={config.GOOGLE_MAPS_ID}
                  >
                    <MapSetup onMapReady={setExpandedMapInstance} />
                    {effectiveOrders
                      .filter(
                        (o) =>
                          o.type.includes("PICKUP") &&
                          o.pickupLocation &&
                          o.pickupLocation.coordinates.length >= 2,
                      )
                      .map((order) => (
                        <Marker
                          key={`exp-pickup-${order.id}`}
                          icon={getOrderIcon(order, "PICKUP")}
                          position={{
                            lat: order.pickupLocation.coordinates[1],
                            lng: order.pickupLocation.coordinates[0],
                          }}
                          routePosition={orderIndexMap[order.id]}
                          routePositionColor={getRoutePositionColor(
                            order.statusText,
                          )}
                          scale={1}
                          type="pickup"
                        />
                      ))}
                    {effectiveOrders
                      .filter(
                        (o) =>
                          o.type.includes("DROPOFF") &&
                          o.dropoffLocation &&
                          o.dropoffLocation.coordinates.length >= 2,
                      )
                      .map((order) => (
                        <Marker
                          key={`exp-dropoff-${order.id}`}
                          icon={getOrderIcon(order, "DROPOFF")}
                          position={{
                            lat: order.dropoffLocation.coordinates[1],
                            lng: order.dropoffLocation.coordinates[0],
                          }}
                          routePosition={orderIndexMap[order.id]}
                          routePositionColor={getRoutePositionColor(
                            order.statusText,
                          )}
                          scale={1}
                          type="dropoff"
                        />
                      ))}
                    <MapPolyline
                      polylineOptions={getRoutePolylineOptions(
                        routeStatusForPolyline,
                        1,
                        isAssigned,
                      )}
                      routeCoordinates={routeCoordinates}
                    />
                    {filteredRoutePoints.map((point, index) => (
                      <AdvancedMarker
                        key={index}
                        position={{ lat: point.lat, lng: point.lng }}
                        onMouseEnter={() => setHoveredPoint(point)}
                        onMouseLeave={() => setHoveredPoint(null)}
                      >
                        <div
                          style={{
                            backgroundColor: "#94A3B8",
                            border: "1px solid #64748B",
                            borderRadius: "50%",
                            height: "6px",
                            opacity: 0.6,
                            width: "6px",
                          }}
                        />
                      </AdvancedMarker>
                    ))}
                    {hoveredPoint && (
                      <InfoWindow
                        position={{
                          lat: hoveredPoint.lat + 0.0001,
                          lng: hoveredPoint.lng,
                        }}
                        pixelOffset={[0, -10]}
                        disableAutoPan
                      >
                        <div className="p-2">
                          <div className="text-sm font-medium">
                            {assigneeName || "Driver"}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            <DateTimeTz
                              start={new Date(hoveredPoint.timestamp)}
                              timezone={timezone}
                              presentation="default"
                              includeSeconds
                            />
                          </div>
                        </div>
                      </InfoWindow>
                    )}
                    {eventMarkers?.placed?.map((event, index) => (
                      <AdvancedMarker
                        key={`expanded-event-${index}`}
                        position={{ lat: event.lat, lng: event.lng }}
                        onMouseEnter={() => setHoveredEventMarker(event)}
                        onMouseLeave={() => setHoveredEventMarker(null)}
                      >
                        <div
                          style={{
                            backgroundColor: event.color,
                            border: "3px solid #FFFFFF",
                            borderRadius: "50%",
                            height: "16px",
                            width: "16px",
                          }}
                        />
                      </AdvancedMarker>
                    ))}
                    {hoveredEventMarker && (
                      <InfoWindow
                        position={{
                          lat: hoveredEventMarker.lat + 0.0001,
                          lng: hoveredEventMarker.lng,
                        }}
                        pixelOffset={[0, -10]}
                        disableAutoPan
                      >
                        <div className="p-2">
                          <div className="text-sm font-semibold text-gray-900 mb-1">
                            {hoveredEventMarker.name}
                          </div>
                          {hoveredEventMarker.orderName && (
                            <div className="text-xs text-gray-600 mb-1">
                              {hoveredEventMarker.orderName}
                            </div>
                          )}
                          <div className="text-xs text-gray-600">
                            <DateTimeTz
                              start={new Date(hoveredEventMarker.timestamp)}
                              timezone={timezone}
                              presentation="default"
                              includeSeconds
                            />
                          </div>
                        </div>
                      </InfoWindow>
                    )}
                  </Map>
                </APIProvider>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default RouteDriverRoute;
